import type { RefObject } from "react";

import { Button } from "@/components/ui/Button";
import { ConfirmDialogContext } from "@/components/ui/confirm-dialog-context";
import { Dialog } from "@/components/ui/Dialog";
import { Sheet } from "@/components/ui/Sheet";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * The one destructive-action pattern every later story reuses (DESIGN.md
 * "Confirm dialog": "a configuration of `dialog`, not a separate
 * component"). This is the **only** place `Button`'s `destructive` variant
 * is instantiated anywhere in the codebase — enforced two ways (VC-2.6-09):
 * a source-text scan (`frontend/tests/architecture/
 * destructive-button-guard.test.ts`) catches the common literal-string case
 * cheaply, and `Button.tsx` itself throws at render time for anything else,
 * checking `ConfirmDialogContext` (which only this component ever sets to
 * `true`) — closing every way of *computing* "destructive" a static scan
 * cannot reliably enumerate (a spread prop, a ternary, a variable).
 *
 * Picks `Dialog` (desktop) or `Sheet` (phone) by viewport — the one place
 * that decision is made for every confirm dialog in the app — and always
 * passes `dismissible={false}`: `Esc`, the scrim, and any outside click are
 * refused (VC-2.6-02); only Cancel or the confirm button can close it.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The dialog's title (`{typography.title}`) — a short noun phrase, not
   * the consequence sentence itself. */
  title: string;
  /** The plain-language consequence: what will happen and what remains
   * (VC-2.6-07). Never the API's raw field names or an implied restore
   * window that does not exist. */
  description: string;
  /** The confirming button's label, verb-first and naming the target —
   * "Remove Sarah", "Delete this view" — never "OK" or "Yes" (VC-2.6-08). */
  confirmLabel: string;
  onConfirm: () => void;
  /** Disables both buttons and swaps the confirm label while the request
   * is in flight, so a double-tap cannot fire the mutation twice. */
  isConfirming?: boolean;
  confirmingLabel?: string;
  cancelLabel?: string;
  /** A failed confirm renders its error here, beside the action that
   * failed — never as a toast (DESIGN.md: "errors do not use toasts"). */
  errorMessage?: string;
  /**
   * Where to send focus on close when the trigger that opened this dialog
   * is gone — confirming almost always unmounts the trigger (the row or
   * chip it belonged to) as part of the very same action. See
   * `Dialog.tsx`'s own doc / `lib/overlay-gate.ts`'s `focusReturnElement`
   * for the live-found bug this covers. Every real caller should supply
   * one; there is no sensible generic default this component could invent
   * on its own.
   */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  isConfirming = false,
  confirmingLabel,
  cancelLabel = "Cancel",
  errorMessage,
  fallbackFocusRef,
}: ConfirmDialogProps) {
  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  return (
    <Container
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      dismissible={false}
      {...(fallbackFocusRef ? { fallbackFocusRef } : {})}
    >
      <ConfirmDialogContext.Provider value={true}>
        {errorMessage ? (
          <p role="alert" className="mt-4 text-meta text-danger">
            {errorMessage}
          </p>
        ) : null}
        <div className="mt-6 flex flex-col-reverse gap-3 md:flex-row md:justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
            className="w-full md:w-auto"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isConfirming}
            className="w-full md:w-auto"
          >
            {isConfirming ? (confirmingLabel ?? confirmLabel) : confirmLabel}
          </Button>
        </div>
      </ConfirmDialogContext.Provider>
    </Container>
  );
}
